import {MarketPlaceTradeStatus} from '@afriex/core/domain/enums';

import type {
  AfriexAssetAmount,
  MarketPlaceChannel,
  MarketPlaceTrade,
} from '@/types/MarketPlace';

import {formatOptionalDate} from '../../utils/date';

// Same pattern the server validates `pair` against.
export const PAIR_PATTERN = /^[A-Z]{3,5}-[A-Z]{3,5}$/;

// The rail a trade was funded on, in Finance's vocabulary. CRYPTO settles
// USDC/USDT into the USD wallet.
export const MARKETPLACE_CHANNEL_LABELS: Record<MarketPlaceChannel, string> = {
  CRYPTO: 'Crypto (USDC/USDT)',
  VIRTUAL_BANK_ACCOUNT: 'Bank transfer',
  POOL_ACCOUNT: 'Pool account',
};

export const formatMarketPlaceChannel = (
  channel?: MarketPlaceChannel,
): string => (channel ? MARKETPLACE_CHANNEL_LABELS[channel] : '-');

const STATUS_LABELS: Record<MarketPlaceTradeStatus, string> = {
  [MarketPlaceTradeStatus.SETTLING]: 'Settling',
  [MarketPlaceTradeStatus.SETTLED]: 'Settled',
  [MarketPlaceTradeStatus.EXPIRED]: 'Expired',
};

//yellow-* is the amber palette in this Tailwind config; amber-* emits no CSS here.
const STATUS_CLASSNAMES: Record<MarketPlaceTradeStatus, string> = {
  [MarketPlaceTradeStatus.SETTLING]: 'text-yellow-700 dark:text-yellow-400',
  [MarketPlaceTradeStatus.SETTLED]: 'text-green-700 dark:text-green-400',
  [MarketPlaceTradeStatus.EXPIRED]: 'text-red-600 dark:text-red-400',
};

/**
 * Groups the integer part and keeps the fractional digits exactly as the
 * server sent them. `Number(value).toLocaleString()` is what this replaces:
 * it silently rounds long decimals on monetary amounts.
 */
export const formatDecimal = (value: string | number): string => {
  const text = String(value);
  const negative = text.startsWith('-');
  const unsigned = negative ? text.slice(1) : text;
  const [integer, fraction] = unsigned.split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formatted = fraction === undefined ? grouped : `${grouped}.${fraction}`;
  return negative ? `-${formatted}` : formatted;
};

export const formatAmount = (amount?: AfriexAssetAmount): string =>
  amount ? `${formatDecimal(amount.value)} ${amount.asset}` : '-';

export const formatCount = (value: number): string =>
  value.toLocaleString('en-US');

export const getTradeStatusLabel = (status: MarketPlaceTradeStatus): string =>
  STATUS_LABELS[status] ?? status;

export const getTradeStatusClassName = (
  status: MarketPlaceTradeStatus,
): string => STATUS_CLASSNAMES[status] ?? 'text-gray-600';

export const getTradePair = (trade: MarketPlaceTrade): string =>
  `${trade.volume.asset}-${trade.counterAmount.asset}`;

export const getTradeDate = (value?: string): string =>
  formatOptionalDate(value);

/** 0-100, clamped. Display only: the underlying values stay as sent. */
export const getFilledPercentage = (
  volume: AfriexAssetAmount,
  filled?: AfriexAssetAmount,
): number => {
  const total = Number(volume.value);
  const used = Number(filled?.value ?? 0);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(used)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round((used / total) * 100)));
};

export const TRADE_STATUS_OPTIONS: {key: string; value: string}[] = [
  {key: 'Settling', value: MarketPlaceTradeStatus.SETTLING},
  {key: 'Settled', value: MarketPlaceTradeStatus.SETTLED},
  {key: 'Expired', value: MarketPlaceTradeStatus.EXPIRED},
];
